/**
 * StationOverlay.js
 *
 * Orbital trading station modal. Reachable while flying in space mode when the
 * ship enters a station's dock radius.
 *
 * Stations buy from BOTH the player's personal backpack AND the ship's cargo
 * hold — unlike planet-side city kiosks which only buy from the ship. Stations
 * don't progress trade_route contracts (those still require a city sell).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { toastRepChanges } from '../lib/factionToast';
import AlloySellPanel from './AlloySellPanel';

export default function StationOverlay({
  open,
  station,       // { id, name, distance }
  coordsKey,
  api,
  player,
  updateCredits,
  onClose,
  onOpenHangar,  // June 2026 — open the Hangar / Shipyard from the station
  onOpenSkills,  // June 2026 — open the Skill Tree from the station
}) {
  const [busy, setBusy] = useState(false);
  // Phase A — controlling faction + standing at this station's system
  const [factionInfo, setFactionInfo] = useState(null);
  const inventory = player?.inventory || {};
  const ores = inventory.ores || [];
  const ship = inventory.ship_cargo || [];

  const backpackUnits = useMemo(
    () => ores.reduce((a, o) => a + (o.qty || 0), 0),
    [ores],
  );
  const shipUnits = useMemo(
    () => ship.reduce((a, o) => a + (o.qty || 0), 0),
    [ship],
  );
  const backpackValue = useMemo(
    () => ores.reduce((a, o) => a + (o.qty || 0) * (o.est_value_per_unit || 0), 0),
    [ores],
  );
  const shipValue = useMemo(
    () => ship.reduce((a, o) => a + (o.qty || 0) * (o.est_value_per_unit || 0), 0),
    [ship],
  );

  const handleSell = useCallback(async (source) => {
    if (!station || !coordsKey) return;
    setBusy(true);
    try {
      const { data } = await api.post('/api/station/sell', {
        coords_key: coordsKey,
        station_id: station.id,
        source,
      });
      if (data?.credits != null) updateCredits?.(data.credits);
      await player?.refreshInventory?.();
      const paid = Math.round(data?.total_paid || 0);
      const label = source === 'both' ? 'all cargo' : (source === 'player' ? 'backpack' : 'ship cargo');
      toast.success(`Sold ${label} · +${paid} cr${data?.faction_bonus > 0 ? ` (incl. +${Math.round(data.faction_bonus)} faction bonus)` : ''}`);
      toastRepChanges(data?.rep_changes);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sell failed');
    } finally {
      setBusy(false);
    }
  }, [api, station, coordsKey, updateCredits, player]);

  // ESC closes the overlay
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Phase A — fetch the station system's controlling faction + my standing
  useEffect(() => {
    if (!open || !coordsKey) return;
    api.get(`/api/factions/system?coords_key=${encodeURIComponent(coordsKey)}`)
      .then((r) => setFactionInfo(r.data)).catch(() => {});
  }, [open, coordsKey, api]);

  if (!open || !station) return null;

  return (
    <div
      className="fixed inset-0 z-[2100] flex items-center justify-center"
      data-testid="station-overlay"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="relative z-10 w-[min(960px,94vw)] max-h-[88vh] overflow-y-auto rounded-xl border border-amber-400/30 bg-[#0a0606]/95 shadow-[0_0_60px_rgba(251,191,36,0.18)]"
        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-amber-400/15">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-amber-300/80">
              Orbital Trading Station
            </div>
            <div className="text-2xl text-amber-100 mt-0.5" data-testid="station-name">
              {station.name}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              Stations buy from both your backpack AND your ship.
            </div>
            {factionInfo?.faction && (
              <div className="text-xs mt-1.5" data-testid="station-faction-line">
                <span className="font-semibold" style={{ color: factionInfo.faction.color }}>
                  {factionInfo.faction.short} space
                </span>
                <span className="text-slate-400"> · standing: </span>
                <span className="uppercase" style={{ color: factionInfo.faction.color }}>
                  {factionInfo.standing?.tier}
                </span>
                {factionInfo.standing?.sell_bonus > 0 && (
                  <span className="text-emerald-300"> · +{Math.round(factionInfo.standing.sell_bonus * 100)}% sell bonus</span>
                )}
                {factionInfo.standing?.refused && (
                  <span className="text-red-400 font-semibold"> · SERVICE REFUSED</span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="border-amber-400/40 text-amber-200 font-mono">
              {(station.distance || 0).toFixed(0)} u
            </Badge>
            {onOpenHangar && (
              <Button
                size="sm"
                onClick={onOpenHangar}
                data-testid="station-open-hangar-button"
                className="bg-fuchsia-500/90 hover:bg-fuchsia-400 text-white font-semibold"
              >
                Hangar
              </Button>
            )}
            {onOpenSkills && (
              <Button
                size="sm"
                onClick={onOpenSkills}
                data-testid="station-open-skills-button"
                className="bg-teal-500/90 hover:bg-teal-400 text-slate-900 font-semibold"
              >
                Skills
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              data-testid="station-close-button"
            >
              Undock
            </Button>
          </div>
        </div>

        {/* Two cargo columns */}
        <div className="grid md:grid-cols-2 gap-4 p-4">
          <CargoColumn
            title="Personal Backpack"
            accent="cyan"
            items={ores}
            units={backpackUnits}
            value={backpackValue}
            onSell={() => handleSell('player')}
            disabled={busy || ores.length === 0}
            testId="station-player-column"
          />
          <CargoColumn
            title="Ship Cargo Hold"
            accent="amber"
            items={ship}
            units={shipUnits}
            value={shipValue}
            onSell={() => handleSell('ship')}
            disabled={busy || ship.length === 0}
            testId="station-ship-column"
          />
        </div>

        {/* Phase 2 — sell refined alloys (full price at a station) */}
        <div className="px-4 pb-4">
          <AlloySellPanel
            api={api}
            terminal="station"
            onSold={(data) => {
              if (data?.credits != null) updateCredits?.(data.credits);
              player?.refreshInventory?.();
            }}
          />
        </div>

        {/* Sell-everything footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-amber-400/15 bg-[#160a0a]/70">
          <div className="text-sm text-slate-300">
            Combined value{' '}
            <span className="text-amber-300 font-mono tabular-nums">
              ≈ {Math.round(backpackValue + shipValue).toLocaleString()} cr
            </span>
          </div>
          <Button
            onClick={() => handleSell('both')}
            disabled={busy || (ores.length === 0 && ship.length === 0)}
            className="bg-amber-500/90 hover:bg-amber-400 text-slate-900 font-semibold tracking-[0.2em] uppercase"
            data-testid="station-sell-all-button"
          >
            Sell Everything
          </Button>
        </div>
      </div>
    </div>
  );
}

function CargoColumn({ title, accent, items, units, value, onSell, disabled, testId }) {
  const isAmber = accent === 'amber';
  const borderCls = isAmber ? 'border-amber-400/25' : 'border-cyan-400/25';
  const titleCls = isAmber ? 'text-amber-200' : 'text-cyan-200';
  const valueCls = isAmber ? 'text-amber-300' : 'text-cyan-300';
  const btnCls = isAmber
    ? 'bg-amber-500/90 hover:bg-amber-400 text-slate-900'
    : 'bg-cyan-500/90 hover:bg-cyan-400 text-slate-900';
  return (
    <Card className={`bg-[#02060f]/40 ${borderCls}`} data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <span className={titleCls}>{title}</span>
          <span className={`text-sm font-mono ${valueCls}`}>
            {units.toFixed(1)} u
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="py-6 text-center text-slate-500 italic text-sm">
            Empty
          </div>
        ) : (
          <div className="space-y-1.5 mb-3">
            {items.map((o) => (
              <div
                key={o.symbol}
                className={`flex items-center justify-between px-3 py-1.5 rounded border ${borderCls} bg-black/30`}
              >
                <div className="flex items-center gap-2">
                  <span className={`font-mono text-xs ${valueCls}`}>{o.symbol}</span>
                  <span className="text-sm text-slate-200">{o.name}</span>
                </div>
                <div className="text-xs text-slate-400 font-mono tabular-nums">
                  {(o.qty || 0).toFixed(2)} u
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between text-xs mb-3">
          <span className="text-slate-400">Est. value</span>
          <span className={`font-mono tabular-nums ${valueCls}`}>
            {Math.round(value).toLocaleString()} cr
          </span>
        </div>
        <Button
          onClick={onSell}
          disabled={disabled}
          className={`w-full text-[10px] tracking-[0.25em] uppercase font-semibold ${btnCls}`}
          data-testid={`${testId}-sell-button`}
        >
          Sell {title.split(' ')[0]}
        </Button>
      </CardContent>
    </Card>
  );
}
